import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Server, Layers, Lock, Globe, Workflow, ArrowRight, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/layout/ScrollReveal';

const CAPABILITIES = [
  { icon: Cpu, title: 'Compute Engine', desc: 'High-performance processing layer optimized for digital product delivery and real-time operations.' },
  { icon: Server, title: 'Infrastructure Core', desc: 'Scalable backend systems designed for multi-product architectures and zero-downtime deployment.' },
  { icon: Layers, title: 'Platform Layer', desc: 'Unified platform services including auth, data, storage, and API management across ventures.' },
  { icon: Lock, title: 'Security Framework', desc: 'Enterprise-grade security protocols with end-to-end encryption and compliance readiness.' },
  { icon: Globe, title: 'Global Distribution', desc: 'Multi-region infrastructure ensuring low-latency access and reliable service worldwide.' },
  { icon: Workflow, title: 'Integration Bus', desc: 'Seamless connectivity layer linking products, services, and third-party systems at scale.' },
];

const PRINCIPLES = [
  { num: '01', title: 'Build for Scale', desc: 'Every system is architected to handle exponential growth from day one.' },
  { num: '02', title: 'Prove Then Ship', desc: 'Test core workflows in isolation. Fix until proven. Then build around it.' },
  { num: '03', title: 'Zero Downtime', desc: 'Infrastructure designed for continuous deployment without service interruption.' },
  { num: '04', title: 'Data-Driven', desc: 'Every decision backed by metrics. Every metric tied to business outcomes.' },
];

export default function AboutPage() {
  useEffect(() => { document.title = 'About'; }, []);
  return (
    <PageTransition>
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-fire-engine" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal hero>
            <div className="flex items-center gap-2 mb-5"><Flame className="h-4 w-4 text-[#E8722A]" /><span className="kicker">About Visualte</span></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">The engine behind<br />digital ventures</h1>
            <p className="text-base md:text-lg text-white/72 leading-relaxed max-w-2xl">Visualte is not a product. It is the infrastructure layer — the foundational power engine that drives, connects, and scales digital businesses. From concept to global deployment, Visualte provides the backbone that makes products possible.</p>
          </ScrollReveal>
        </div>
      </section>
      <section className="py-16 sm:py-20 lg:py-28 relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ScrollReveal>
              <span className="kicker">Mission</span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-6">Build the control plane for digital business</h2>
              <p className="text-sm md:text-base text-white/72 leading-relaxed">We create the systems, tools, and infrastructure that digital brands need to launch, operate, and scale. Visualte handles the complexity so products can focus on delivering value.</p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="hud-card border border-white/10 bg-white/[0.03] p-8">
                <span className="kicker block mb-4">Visualte in Numbers</span>
                <div className="space-y-5">
                  <div><div className="text-3xl font-semibold text-[#E8722A] font-mono">99.9%</div><div className="text-xs text-white/55 mt-1">Infrastructure Uptime</div></div>
                  <Separator className="bg-white/8" />
                  <div><div className="text-3xl font-semibold text-white font-mono">1</div><div className="text-xs text-white/55 mt-1">Live Powered Product</div></div>
                  <Separator className="bg-white/8" />
                  <div><div className="text-3xl font-semibold text-white font-mono">Global</div><div className="text-xs text-white/55 mt-1">Infrastructure Distribution</div></div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      <section className="py-16 sm:py-20 lg:py-28 relative">
        <div className="absolute inset-0 bg-ember-glow" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal><span className="kicker">Capabilities</span><h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-12">What Visualte provides</h2></ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {CAPABILITIES.map((cap) => (
              <StaggerItem key={cap.title}>
                <div className="hud-card border border-white/10 bg-white/[0.03] p-6 h-full card-hover-glow">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(232,114,42,0.06)] border border-[rgba(232,114,42,0.12)] flex items-center justify-center mb-4">
                    <cap.icon className="h-5 w-5 text-[#E8722A]" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2">{cap.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{cap.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
      <section className="py-16 sm:py-20 lg:py-28 relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal><span className="kicker">Operating Principles</span><h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-12">How we build</h2></ScrollReveal>
          <div className="space-y-0">
            {PRINCIPLES.map((p, i) => (
              <ScrollReveal key={p.num} delay={i * 0.08}>
                <div className="border-b border-white/8 py-8 flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <span className="text-xs font-mono text-[#E8722A] mt-1">{p.num}</span>
                  <div><h3 className="text-xl font-semibold tracking-[-0.02em] mb-2">{p.title}</h3><p className="text-sm text-white/55 leading-relaxed max-w-lg">{p.desc}</p></div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 sm:py-20 lg:py-28 relative">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">Power your next venture</h2>
            <Link to="/contact"><Button className="btn-primary-gradient btn-shimmer font-semibold h-12 px-8 text-base rounded-lg">Discuss Business <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </ScrollReveal>
        </div>
      </section>
    </PageTransition>
  );
}
